import { useEffect, useState } from 'react'

const SSO_STORAGE_KEY = 'sso_tokens'

interface SsoPageProps {
  platform: string
  apiBaseUrl: string
  loginPath?: string
}

/**
 * SSO callback page.
 * Reads the token from the URL, exchanges it via the backend,
 * stores Keycloak tokens, and redirects to the app root.
 */
export function SsoPage({ platform, apiBaseUrl, loginPath = '/auth/keycloak/redirect' }: SsoPageProps) {
  const [error, setError] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')

    // Immediately remove token from URL
    window.history.replaceState({}, '', '/sso')

    if (!token) {
      setError(true)
      return
    }

    fetch(`${apiBaseUrl}/sso-exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, platform }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('exchange failed')
        return res.json()
      })
      .then((body) => {
        const data = body.data || body
        // Store tokens so the Keycloak init can pick them up
        sessionStorage.setItem(
          SSO_STORAGE_KEY,
          JSON.stringify({
            token: data.access_token,
            refreshToken: data.refresh_token,
            idToken: data.id_token,
          })
        )
        // Navigate to app root — Keycloak provider will init with stored tokens
        window.location.href = '/'
      })
      .catch(() => {
        setError(true)
      })
  }, [])

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'sans-serif' }}>
        <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>
          Lien de connexion invalide ou expiré
        </p>
        <a href={loginPath} style={{ color: '#6366f1', textDecoration: 'underline' }}>
          Se connecter
        </a>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'sans-serif' }}>
      <p>Connexion en cours...</p>
    </div>
  )
}

/**
 * Read stored SSO tokens (if any) and return Keycloak init overrides.
 * Tokens are kept in sessionStorage so they survive page refresh.
 */
export function getSsoInitTokens(): { token: string; refreshToken: string; idToken: string } | null {
  try {
    const raw = sessionStorage.getItem(SSO_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * Update stored SSO tokens when Keycloak refreshes them.
 * Call this from the ReactKeycloakProvider onTokens callback.
 */
export function updateSsoTokens(token?: string, refreshToken?: string, idToken?: string): void {
  if (!token) return
  // Only update if we have an SSO session (not a normal Keycloak login)
  if (!sessionStorage.getItem(SSO_STORAGE_KEY)) return
  sessionStorage.setItem(
    SSO_STORAGE_KEY,
    JSON.stringify({ token, refreshToken, idToken })
  )
}

/**
 * Clear SSO tokens (call on logout).
 */
export function clearSsoTokens(): void {
  sessionStorage.removeItem(SSO_STORAGE_KEY)
}

export { SSO_STORAGE_KEY }
